import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Shield, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";

const LOGO = "https://customer-assets.emergentagent.com/job_jci-content-platform/artifacts/mv5wtmf7_Asset%203.png";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState("");
  const { login, admin } = useAuth();
  const navigate = useNavigate();
  usePageTitle("Admin Login");

  if (admin) { navigate("/admin/dashboard"); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please fill in all fields"); return; }
    setLoading(true);
    try {
      if (isRegister) {
        const res = await api.post("/auth/register", { email, password, name });
        localStorage.setItem("admin_token", res.data.token);
        toast.success("Account created! Redirecting...");
        window.location.href = "/admin/dashboard";
      } else {
        await login(email, password);
        toast.success("Welcome back!");
        navigate("/admin/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-jci-black flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO} alt="JCI Bangladesh" className="h-14 mx-auto mb-6" />
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-jci-blue" />
            <h1 className="text-xl font-bold text-white">Admin Panel</h1>
          </div>
          <p className="text-white/40 text-sm">JCI Bangladesh Content Management System</p>
        </div>
        <Card className="border-white/10 bg-white/5 backdrop-blur-md">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
              {isRegister && (
                <div>
                  <Label className="text-white/70 text-sm">Name</Label>
                  <Input data-testid="admin-name" value={name} onChange={e => setName(e.target.value)} placeholder="Admin Name" className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/30" />
                </div>
              )}
              <div>
                <Label className="text-white/70 text-sm">Email</Label>
                <Input data-testid="admin-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@jcibangladesh.org" className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              </div>
              <div>
                <Label className="text-white/70 text-sm">Password</Label>
                <div className="relative mt-1.5">
                  <Input data-testid="admin-password" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pr-10" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" data-testid="admin-submit" disabled={loading} className="w-full bg-jci-blue hover:bg-jci-navy text-white font-semibold">
                {loading ? "Please wait..." : isRegister ? "Create Account" : "Sign In"}
              </Button>
              <button type="button" onClick={() => setIsRegister(!isRegister)} className="w-full text-center text-sm text-jci-teal hover:text-jci-blue transition-colors" data-testid="admin-toggle-mode">
                {isRegister ? "Already have an account? Sign In" : "First time? Create Admin Account"}
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
